/**
 * @file: The order of a playlist, generated instead of typed.
 *
 * A playlist loops on a screen for a whole day. The operators want the same
 * clip to come back at even intervals. They do not want two clips of the same
 * kind next to each other. A uniform shuffle gives neither.
 *
 * The hand-made lists from RF26 follow three rules, measured on the stored
 * order. The same media does not return within four slots. Repeated media sit
 * at nearly equal gaps. Two neighbours are rarely of the same kind. A fourth
 * rule is in time: the same media does not return within a minute, because
 * four ten-second slots are only forty seconds.
 *
 * This module gives that order from a list of rows. Every row has a key (the
 * media and its duration), a kind (the label that separates it from its
 * neighbours) and a duration. The order is deterministic for a seed.
 *
 * The client computes the order today and the server stores what it gets. The
 * module is in shared/ because the server needs the same rules the day it
 * derives a list itself, for a takeover copy or a report. With one
 * implementation, the preview on the client and the stored order stay the same.
 *
 * The algorithm has two steps. The spread places each kind at even positions
 * with a small random offset, then each key inside its kind the same way, and
 * sorts. This is the balanced shuffle. The repair then swaps random pairs of
 * unpinned slots, and then tries every pair. A swap is kept when it lowers the
 * penalty of the rule breaks, or when it keeps that penalty and makes the
 * waits better: the waits between plays of a repeated key more even, or the
 * short waits between plays of the same kind longer. The rules come first,
 * and no swap trades a rule for the waits.
 *
 * The repair can stop at a local optimum: the one swap that would even the
 * waits breaks a rule, so it is refused, and no other swap helps. So the two
 * steps run from several starts of the same seed, and the best result wins.
 */

import { groupBy } from './group-by.js'

const DEFAULT_RULES = Object.freeze({
  minGap: 4,
  minGapMs: 60000,
  sameKindApart: true,
  shortAfterLong: true,
  longMs: 35000
})

const WEEKDAYS = Object.freeze([
  'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag',
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
])

const ALL_DAYS = Object.freeze(['alle dage', 'all days'])

const NOT_A_KIND = new Set([...WEEKDAYS, ...ALL_DAYS])

/**
 * The random swaps of one start, before the walk through every pair. With the
 * walk, 300 steps give the same result as 1200, so the steps are only there
 * to leave the balanced spread in a different way for each start.
 */
const REPAIR_STEPS = 300

/**
 * How many times the repair walks through every pair of slots. Two passes
 * take half the time of six on a list of 33 rows, and the waits end within a
 * few seconds of the same evenness.
 */
const SWEEP_PASSES = 2

const REPAIR_RESTARTS = 4

/**
 * How much the crowding of the kinds counts against the unevenness of the
 * repeated keys, in the score of the repair. Measured on two real lists over
 * thirty seeds: at 0.5 the pairs of the same kind within two slots fall to a
 * tenth and a third, and the waits of the repeated keys stay as even as
 * before. At 1 the waits of the keys drift apart and more rules break.
 */
const KIND_SHORTFALL_WEIGHT = 0.5
const JITTER = 0.35

/**
 * A small, fast, seeded generator (mulberry32). Math.random cannot be seeded,
 * and the client and the server must draw the same numbers for the same seed.
 */
function seededRandom (seed) {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6D2B79F5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * The grouping key of a row. Rows of the same media with different durations
 * are different lines in the recipe, because a social item's duration is its
 * number of posts.
 */
function rowKey ({ mediaType, mediaId, duration }) {
  return `${mediaType}:${mediaId}:${duration}`
}

const labelName = label => (typeof label === 'string' ? label : label.name)

/**
 * A test for one label. A label means a day, a scene or a kind, and all three
 * are in one table. The kind is what is left when the day and the scene are
 * removed.
 *
 * The set of scene names is built one time, because the caller runs this test
 * over every label of every media.
 *
 * @param {object} [options] - Holds the scene names of the app
 * @returns {Function} A test that takes a label and gives true or false
 */
function kindLabelTest ({ sceneNames = [] } = {}) {
  const scenes = new Set(sceneNames.map(one => String(one).toLowerCase()))

  return label => {
    const name = String(labelName(label) || '').toLowerCase()

    return name !== '' && !NOT_A_KIND.has(name) && !scenes.has(name)
  }
}

/**
 * Whether one label is a kind. A caller with a list of labels builds the test
 * one time with kindLabelTest instead.
 *
 * @param {object|string} label - One label, or the name of one
 * @param {object} [options] - Holds the scene names of the app
 * @returns {boolean} True when the label is a kind
 */
const isKindLabel = (label, options) => kindLabelTest(options)(label)

/**
 * The label that separates a media from its neighbours. Null when the media has
 * no kind label.
 *
 * When two kind labels are left, the first by name wins. The database gives
 * the labels of a media in no fixed order, and the kind must not change between
 * two loads.
 */
function kindOf (labels, options = {}) {
  const isKind = kindLabelTest(options)
  const kinds = (labels || []).filter(isKind).map(labelName)

  return kinds.length ? kinds.sort((a, b) => a.localeCompare(b))[0] : null
}

/**
 * Even positions in [0, 1) for a group of n, with a shared random offset and a
 * small jitter per position. The offset stops every group from starting at
 * zero, and the jitter stops the result from looking mechanical.
 */
function evenPositions (n, random) {
  const offset = random()
  return Array.from({ length: n }, (_, i) => ((i + random() * JITTER) / n + offset) % 1)
}

const byPosition = (a, b) => a.position - b.position

/** Spread the rows of one kind: each key evenly over the slots the kind got. */
function spreadKind (rows, slots, random) {
  const placed = [...groupBy(rows, row => row.key).values()]
    .flatMap(sameKey => evenPositions(sameKey.length, random).map((position, i) => ({ position, row: sameKey[i] })))
    .sort(byPosition)
  return placed.map((entry, i) => ({ position: slots[i], row: entry.row }))
}

/** Spread every kind over the whole list, then every key inside its kind. */
function balancedSpread (rows, random) {
  return [...groupBy(rows, row => row.kind ?? '').values()]
    .flatMap(sameKind => spreadKind(sameKind, evenPositions(sameKind.length, random).sort((a, b) => a - b), random))
    .sort(byPosition)
    .map(entry => entry.row)
}

const circularGap = (i, j, n) => Math.min(Math.abs(j - i), n - Math.abs(j - i))

/** The largest gap a key can keep: its plays share the slots evenly. */
const largestGap = (n, count) => Math.floor(n / count)

/**
 * The rule breaks in an order, as data. The client turns them into sentences.
 * The list is a loop, so the last slot is a neighbour of the first.
 *
 * A key with too many plays for the list cannot keep the minimum gap. No order
 * can fix that. It is reported once as a 'count' break, and the repeats of that
 * key are checked against the gap it can keep. So the repair can reach zero.
 */
function violations (rows, rules = DEFAULT_RULES) {
  return violationsWith(rows, rules, repeatWaits(rows))
}

/**
 * The same, with the waits of the repeated keys given by the caller. Only the
 * repair uses this, because it computes the waits once per step for two goals.
 * The waits must be those of `rows` as they are now.
 */
function violationsWith (rows, rules, waitsOfKeys) {
  const n = rows.length
  if (n < 2) return []
  const total = loopMs(rows)
  const counts = new Map([...groupBy(rows, row => row.key).entries()].map(([key, same]) => [key, same.length]))
  const limitOf = key => Math.min(rules.minGap, largestGap(n, counts.get(key)))
  const largestWait = count => Math.floor(total / count)
  // The wait a key can keep is under its mean wait by up to the longest row, because
  // a boundary between two plays moves by whole rows. Below that the rule is silent
  // and the count break speaks instead.
  const longestRowMs = Math.max(...rows.map(row => row.durationMs))
  const waitLimitOf = key => Math.min(rules.minGapMs, largestWait(counts.get(key)) - longestRowMs)
  const repeated = [...counts.entries()].filter(([, count]) => count > 1)
  // A key with too many plays for the slots, and a key with too many plays for the time: one break each.
  const tooMany = repeated
    .filter(([, count]) => largestGap(n, count) < rules.minGap)
    .map(([key, count]) => ({ rule: 'count', key, count, maxGap: largestGap(n, count), minGap: rules.minGap }))
  const tooManyForTime = repeated
    .filter(([, count]) => total > 0 && largestWait(count) < rules.minGapMs)
    .map(([key, count]) => ({ rule: 'countTime', key, count, maxWaitMs: largestWait(count), minGapMs: rules.minGapMs }))
  // The same media within the minimum time, checked against the wait the key can keep.
  const waits = waitsOfKeys.flatMap(({ key, slots, waits: keyWaits }) => {
    const limit = waitLimitOf(key)
    if (limit <= 0 || largestWait(counts.get(key)) < rules.minGapMs) return []
    return keyWaits
      .map((waitMs, i) => ({ rule: 'wait', slots: [slots[i], slots[(i + 1) % slots.length]], waitMs, minGapMs: limit }))
      .filter(v => v.waitMs < v.minGapMs)
  })
  const repeats = rows.flatMap((row, i) => Array.from({ length: Math.max(0, limitOf(row.key) - 1) }, (_, k) => (i + k + 1) % n)
    .filter(j => rows[j].key === row.key)
    .map(j => ({ rule: 'repeat', slots: [i, j], gap: circularGap(i, j, n), minGap: limitOf(row.key) })))
  // Two rows form one neighbour pair, not two.
  const neighbours = n === 2 ? [[0, 1]] : rows.map((row, i) => [i, (i + 1) % n])
  const kinds = rules.sameKindApart
    ? neighbours.filter(([i, j]) => rows[i].kind != null && rows[i].kind === rows[j].kind).map(slots => ({ rule: 'kind', slots }))
    : []
  const longs = rules.shortAfterLong
    ? neighbours.filter(([i, j]) => rows[i].durationMs >= rules.longMs && rows[j].durationMs >= rules.longMs).map(slots => ({ rule: 'long', slots }))
    : []
  return [...tooMany, ...tooManyForTime, ...repeats, ...waits, ...kinds, ...longs]
}

/**
 * The smallest number of slots between two plays of the same key, or null when
 * nothing repeats. The header of the sequence shows it.
 */
function closestRepeat (rows) {
  const n = rows.length
  const gaps = [...groupBy(rows.map((row, slot) => ({ key: row.key, slot })), entry => entry.key).values()]
    .filter(slots => slots.length > 1)
    .flatMap(slots => slots.map((entry, i) => circularGap(entry.slot, slots[(i + 1) % slots.length].slot, n)))
  return gaps.length > 0 ? Math.min(...gaps) : null
}

/**
 * The weight of one break. A break counts once, whatever its size, so the
 * repair removes breaks before it does anything else. How short a wait or a
 * gap is belongs to the second goal, the evenness. A 'count' or 'countTime'
 * break has no weight: no swap can change it.
 */
const WEIGHT = Object.freeze({ repeat: 10, wait: 10, kind: 2, long: 1 })

function penalty (rows, rules, waitsOfKeys) {
  return violationsWith(rows, rules, waitsOfKeys).filter(v => v.rule in WEIGHT).reduce((sum, v) => sum + WEIGHT[v.rule], 0)
}

const loopMs = rows => rows.reduce((sum, row) => sum + row.durationMs, 0)

/** The start time of every slot, and the loop length last. One pass, one array. */
function startsOf (rows) {
  const starts = new Array(rows.length + 1)
  starts[0] = 0
  rows.forEach((row, i) => { starts[i + 1] = starts[i] + row.durationMs })
  return starts
}

const mean = values => values.reduce((a, b) => a + b, 0) / values.length

/**
 * How uneven the waits of the groups are: for each group with more than one
 * play, the longest wait between two plays minus the shortest, over the mean
 * wait, added up. Zero when every group comes back at the same interval every
 * time. The groups are the repeated keys.
 *
 * Time, not slots. The screen shows time, and a list mixes ten-second clips
 * with two-minute films, so gaps that are even in slots can differ by minutes.
 * Over the mean, so a film that comes back every eight minutes and a clip that
 * comes back every two count alike. Measured in seconds, a swap of two rows of
 * different length moves every start between them, and that small shift on a
 * key that was even costs more than the gain on a kind that was crowded.
 */
function unevenness (waitsOfGroups) {
  return waitsOfGroups.reduce((sum, { waits }) => sum + (Math.max(...waits) - Math.min(...waits)) / mean(waits), 0)
}

/**
 * How crowded the groups are: for each wait that is shorter than the mean wait
 * of its group, the part it falls short by, squared, added up. A long wait
 * costs nothing, because a cluster is what the operator sees, and the long
 * wait it makes elsewhere is the same fault counted twice. The groups are the
 * kinds.
 */
function shortfall (waitsOfGroups) {
  return waitsOfGroups.reduce((sum, { waits }) => {
    const expected = mean(waits)
    return sum + waits.reduce((acc, wait) => acc + Math.max(0, 1 - wait / expected) ** 2, 0)
  }, 0)
}

/**
 * For every group with more than one play: its slots, and the wait in
 * milliseconds from the start of each play to the start of the next, around
 * the loop. `groupOf` names the group of a row; a row with a null group is
 * left out.
 */
function waitsBy (rows, groupOf) {
  const starts = startsOf(rows)
  const total = starts[rows.length]
  const entries = rows.map((row, slot) => ({ key: groupOf(row), slot })).filter(entry => entry.key != null)
  return [...groupBy(entries, entry => entry.key).values()]
    .filter(same => same.length > 1)
    .map(same => {
      const slots = same.map(entry => entry.slot)
      const waits = slots.map((slot, i) => (i + 1 < slots.length ? starts[slots[i + 1]] - starts[slot] : total - starts[slot] + starts[slots[0]]))
      return { key: same[0].key, slots, waits }
    })
}

/**
 * The waits of every repeated key. This is what the operator experiences as
 * "how often it comes back".
 */
const repeatWaits = rows => waitsBy(rows, row => row.key)

/**
 * The waits of every kind with more than one play. The rule about kinds only
 * forbids two neighbours, and the balanced spread is even in fractions, not in
 * slots. So a kind with five plays can land twice within three slots and break
 * no rule. The repair reads the shortfall of these waits, so the plays of a
 * kind move apart when that costs the repeated keys little.
 */
const kindWaits = rows => waitsBy(rows, row => row.kind)

/**
 * The two things the repair minimises, in order: rule breaks, then the waits.
 * The second term is the unevenness of the repeated keys plus the weighted
 * shortfall of the kinds, so one swap can trade a little of one for more of
 * the other. The waits of the repeated keys feed both the penalty and the
 * unevenness, so they are computed once here.
 */
function score (rows, rules) {
  const waitsOfKeys = repeatWaits(rows)
  return [penalty(rows, rules, waitsOfKeys), unevenness(waitsOfKeys) + KIND_SHORTFALL_WEIGHT * shortfall(kindWaits(rows))]
}

/** Negative when score a is better than b, zero when equal. The first term that differs decides. */
const compareScores = (a, b) => a.reduce((result, term, i) => result || term - b[i], 0)
const notWorse = (a, b) => compareScores(a, b) <= 0

const swapInPlace = (rows, i, j) => { [rows[i], rows[j]] = [rows[j], rows[i]] }

/**
 * Swap random pairs of free slots and keep a swap that is not worse: fewer
 * rule breaks, or the same breaks and waits at least as even. Swap back
 * otherwise. Then walk through every pair. Pinned slots never move. The input
 * is not changed: the swaps happen on one copy, so no copy is made per step.
 */
function repair (rows, { random, rules, isPinned, steps }) {
  const work = rows.slice()
  const free = work.map((_, i) => i).filter(i => !isPinned(i))
  // Zero steps means no repair at all. The lab compares the spread alone.
  if (free.length < 2 || steps === 0) return work
  const pick = () => free[Math.floor(random() * free.length)]
  let current = score(work, rules)
  for (let step = 0; step < steps; step++) {
    const i = pick()
    const j = pick()
    if (i === j) continue
    swapInPlace(work, i, j)
    const candidate = score(work, rules)
    if (notWorse(candidate, current)) {
      current = candidate
    } else {
      swapInPlace(work, i, j)
    }
  }
  return sweep(work, { free, rules, current })
}

/**
 * Try every pair of free slots in order and keep each swap that makes the
 * score better, until one pass keeps none or the passes run out. The random
 * steps stop when their count runs out, and a list then often has a pair left
 * that no step tried. The walk finds it. It runs on the copy of the repair.
 */
function sweep (work, { free, rules, current }) {
  let best = current
  let improved = true
  for (let pass = 0; improved && pass < SWEEP_PASSES; pass++) {
    improved = false
    for (let a = 0; a < free.length; a++) {
      for (let b = a + 1; b < free.length; b++) {
        swapInPlace(work, free[a], free[b])
        const candidate = score(work, rules)
        if (better(candidate, best)) {
          best = candidate
          improved = true
        } else {
          swapInPlace(work, free[a], free[b])
        }
      }
    }
  }
  return work
}

/** A fixed order of the rows, so the result does not depend on the order they came in. */
const byCanonical = (a, b) => String(a.key).localeCompare(String(b.key)) || String(a.id).localeCompare(String(b.id))

/**
 * Keep only pins that point at a real row and a real slot, one slot per row.
 * Returns a Map of slot to row id. Exported so the client keeps the pins it
 * shows with the same rule that spread() uses to honour them.
 */
function validPins (pins, rows) {
  const ids = new Set(rows.map(row => row.id))
  const seen = new Set()
  return new Map(Object.entries(pins)
    .map(([slot, id]) => [Number(slot), id])
    .filter(([slot, id]) => Number.isInteger(slot) && slot >= 0 && slot < rows.length && ids.has(id))
    .filter(([, id]) => !seen.has(id) && seen.add(id)))
}

/** One start: spread the free rows around the pinned slots, then repair. */
function arrange (canonical, pinned, { random, rules, steps }) {
  const pinnedIds = new Set(pinned.values())
  const byId = new Map(canonical.map(row => [row.id, row]))
  const free = balancedSpread(canonical.filter(row => !pinnedIds.has(row.id)), random)
  let next = 0
  const placed = canonical.map((_, slot) => (pinned.has(slot) ? byId.get(pinned.get(slot)) : free[next++]))
  return repair(placed, { random, rules, isPinned: slot => pinned.has(slot), steps })
}

const better = (a, b) => compareScores(a, b) < 0

/**
 * A new order for the rows.
 *
 * Each row is `{ id, key, kind, durationMs }`, in any order. `pins` maps a slot
 * index to a row id. A pinned row takes that slot. The other rows are spread
 * over the free slots. The same rows and the same seed give the same order,
 * whatever order the rows came in. The result holds the same row objects in
 * the new order.
 *
 * The order is the best of `restarts` starts. Every start draws from the same
 * seeded generator, so the result is still fixed for a seed. The first start
 * keeps a tie, so one start gives what it gave before.
 */
function spread (rows, { seed = 1, pins = {}, rules = DEFAULT_RULES, repairSteps = REPAIR_STEPS, restarts = REPAIR_RESTARTS } = {}) {
  if (rows.length < 2) return rows.slice()
  const random = seededRandom(seed)
  const canonical = rows.slice().sort(byCanonical)
  const pinned = validPins(pins, canonical)
  const options = { random, rules, steps: repairSteps }
  return Array.from({ length: Math.max(1, restarts) }, () => arrange(canonical, pinned, options))
    .map(order => ({ order, score: score(order, rules) }))
    .reduce((best, candidate) => (better(candidate.score, best.score) ? candidate : best))
    .order
}

const HOUR_MS = 3600000

/**
 * What the operator gets for a count: how often a key comes back, and how many
 * times an hour it is on screen while the list loops. One entry per key, in
 * order of first appearance.
 */
function outcomes (rows) {
  const total = loopMs(rows)
  return [...groupBy(rows, row => row.key).entries()].map(([key, sameKey]) => ({
    key,
    count: sameKey.length,
    itemMs: sameKey[0].durationMs,
    everyMs: total / sameKey.length,
    perHour: total > 0 ? HOUR_MS * sameKey.length / total : 0
  }))
}

/** How much of the loop each kind takes, as a count, as time and as a fraction. */
function kindShares (rows) {
  const total = loopMs(rows)
  return [...groupBy(rows, row => row.kind ?? null).entries()].map(([kind, sameKind]) => {
    const ms = loopMs(sameKind)
    return { kind, count: sameKind.length, ms, fraction: total > 0 ? ms / total : 0 }
  })
}

export {
  DEFAULT_RULES,
  seededRandom,
  rowKey,
  kindOf,
  isKindLabel,
  kindLabelTest,
  spread,
  validPins,
  violations,
  closestRepeat,
  repeatWaits,
  kindWaits,
  outcomes,
  kindShares,
  loopMs
}
